import React__default, { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import { Badge } from '../Badge/Badge.js';
import { Button } from '../Button/Button.js';
import { Card } from '../Card/Card.js';
import classNames from 'classnames';
import { TextArea } from '../TextArea/TextArea.js';

// **** Duplicate Paid Off options due to agreement status naming conventions (in CLA-Miniapp) ****
var StatusLabels;
(function (StatusLabels) {
    StatusLabels["pending"] = "Pending";
    StatusLabels["active"] = "Active";
    StatusLabels["paidOff"] = "Paid Off";
    StatusLabels["frozen"] = "Frozen";
    StatusLabels["defaulted"] = "Defaulted";
    StatusLabels["paid off"] = "Paid Off";
    StatusLabels["construction complete"] = "Construction Complete";
})(StatusLabels || (StatusLabels = {}));
const Status = (props) => {
    const loanOrProject = props.isProject ? 'Project' : 'Loan';
    const [isEditingNote, setIsEditingNote] = useState(false);
    const [note, setNote] = useState(props.note);
    // Handle updates to note from outside component after mount (e.g. from the status update modal):
    useEffect(() => {
        setNote(props.note);
    }, [props === null || props === void 0 ? void 0 : props.note]);
    const handleNoteChange = () => {
        props.handleUpdateNote(note);
        setIsEditingNote(false);
    };
    const getStatusCardClasses = () => {
        return classNames({
            Status__card: true,
            'Status__card--pending': props.status.toLowerCase() === 'pending',
            'Status__card--active': props.status.toLowerCase() === 'active',
        });
    };
    const getStatusHeaderClasses = () => {
        return classNames({
            'Status__header--paidOff': ['paidoff', 'paid off'].includes(props.status.toLowerCase()),
            'Status__header--frozen': props.status.toLowerCase() === 'frozen',
            'Status__header--defaulted': props.status.toLowerCase() === 'defaulted',
            'Status__header--complete': ['complete', 'construction complete'].includes(props.status.toLowerCase()),
        });
    };
    const getStatusCardSubtitleClasses = () => {
        return classNames({
            'Status__subtitle--paidOff': ['paidoff', 'paid off'].includes(props.status.toLowerCase()),
            'Status__subtitle--frozen': props.status.toLowerCase() === 'frozen',
            'Status__subtitle--defaulted': props.status.toLowerCase() === 'defaulted',
            'Status__subtitle--complete': ['complete', 'construction complete'].includes(props.status.toLowerCase()),
        });
    };
    const getStatusCardTextClasses = () => {
        return classNames({
            'Status__text--paidOff': ['paidoff', 'paid off'].includes(props.status.toLowerCase()),
            'Status__text--frozen': props.status.toLowerCase() === 'frozen',
            'Status__text--defaulted': props.status.toLowerCase() === 'defaulted',
            'Status__text--complete': ['complete', 'construction complete'].includes(props.status.toLowerCase()),
        });
    };
    const renderBadges = () => {
        var _a;
        if (!((_a = props.flags) === null || _a === void 0 ? void 0 : _a.length))
            return React__default.createElement(React__default.Fragment, null);
        return (React__default.createElement(Badge, { badgeList: true, className: "Status__flags" },
            props.flags.map((flag, i) => {
                return React__default.createElement(Badge, { key: i, color: flag.color, text: flag.label });
            }),
            React__default.createElement(Button, { key: 1, type: "clear", "data-testid": "Status-edit-flags-button", className: "Status__edit-flags", size: "tiny", icon: { icon: 'pen' }, handleClick: props.handleFlagsClick })));
    };
    const renderFlagLoan = () => {
        if (!props.flagLoan)
            return React__default.createElement(React__default.Fragment, null);
        return (React__default.createElement("div", null,
            React__default.createElement(Button, { key: 2, type: "clear", "data-testid": "Status-flag-loan-button", className: "Status__edit-flags--empty", size: "tiny", iconLeft: { icon: 'flag' }, handleClick: props.handleFlagLoanClick }, "Flag Loan")));
    };
    const renderTextArea = () => {
        return (React__default.createElement(TextArea, { focusOnRender: true, id: "agreement-status-note", handleBlur: handleNoteChange, handleChange: (e) => setNote(e.target.value), name: "note", value: note, placeholder: props.textAreaPlaceholder || getText() }));
    };
    const renderStatusNote = () => {
        return (React__default.createElement("div", { className: "Status__note", "data-testid": "status-note" }, isEditingNote ? (React__default.createElement(React__default.Fragment, null, renderTextArea())) : (React__default.createElement(React__default.Fragment, null,
            React__default.createElement("p", { "data-testid": "status-note-text", className: props.note ? 'Status__note-text' : 'Status__note-text--empty' },
                props.note || getText(),
                ' ',
                React__default.createElement("button", { "data-testid": "update-status-button", onClick: () => setIsEditingNote(true), className: "Status__update-status" }, "Update")),
            props.note && (React__default.createElement("p", { "data-testid": "status-timestamp", className: "Status__timestamp" },
                props.updatedBy,
                " ",
                moment.utc(props.updatedAt).tz(moment.tz.guess()).format('M/D/YY [at] LT')))))));
    };
    const getText = () => {
        if (props.textAreaPlaceholder) {
            return props.textAreaPlaceholder;
        }
        else if (props.isProject) {
            return 'Add project status note...';
        }
        else {
            return 'Add loan status note...';
        }
    };
    const getSubtitleText = () => {
        return props.subtitleOverride || `${loanOrProject} Status`;
    };
    const getButtonText = () => {
        return props.status.toLowerCase() == 'pending' ? `Activate ${loanOrProject}` : `Update ${loanOrProject} Status`;
    };
    return (React__default.createElement("div", { "data-testid": "built-status" },
        React__default.createElement(Card, { isCollapsed: false, "data-testid": "status-card", collapsible: false, noPadding: true, className: `${getStatusCardClasses()}` },
            React__default.createElement("div", { "data-testid": "status-header", className: `Status__header ${getStatusHeaderClasses()}` },
                React__default.createElement("h3", { className: `Status__subtitle ${getStatusCardSubtitleClasses()}` }, getSubtitleText()),
                React__default.createElement("h4", { className: `Status__text ${getStatusCardTextClasses()}`, "data-testid": "status-text" }, (props === null || props === void 0 ? void 0 : props.labelOverride) ? props.labelOverride : StatusLabels[props.status])),
            React__default.createElement("div", { className: "Status__body" },
                renderBadges(),
                renderFlagLoan(),
                renderStatusNote(),
                React__default.createElement("div", { className: "Status__button" },
                    React__default.createElement(Button, { type: "primary", size: "default", isFullWidth: true, className: `${props.status.toLowerCase() == 'pending' ? 'Button--positive' : 'Button--secondary'}`, handleClick: props.handleSubmit, disabled: props.isDisabled, loading: props.isLoading, "data-testid": "built-agreement-setup-activate-button" }, getButtonText()))))));
};
Status.defaultProps = {
    flagLoan: false,
};

export { Status, StatusLabels, Status as default };
//# sourceMappingURL=Status.js.map
